import React, { Fragment, useState } from 'react';
import { Form, Button, Card, Alert } from 'react-bootstrap';

// import translations
import { useTranslation } from 'react-i18next';

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

// import Modals
import ModalShowPassword from './ModalShowPassword';

const DynamicForm = ({ flow, formData, formValues, onChange, onButtonClick, authHeader, authUser }) => {
    const { t } = useTranslation();
    const [notification, setNotification] = useState({ variant: '', title: '', message: '' });

    const password = {
        type: 'showPassword', // Indicate that we want to insert a value
        message: t('Please copy the following credentials to your SFTP or FTP client.'),
        secrets: [
            {
                target: 'username',
                value: ''
            },
            {
                target: 'password',
                value: ''
            }
        ]
    }
            
    // Modals
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showPasswordData, setShowPasswordData] = useState(password);

    // Handler für Button-Klicks
    const handleButtonClick = (field) => {
        if (field.onClick) {
            field.onClick(flow, formValues, authHeader, authUser, (error, response) => {
                if (error) {
                    handleShowNotification(setNotification, 'danger', t('Error'), (error.message + '. Message: ' + response));
                } else {
                    //handleShowNotification(setNotification, 'success', t('Successful'), t('API credentials are valid and can connect to the Kaufland Marketplace Seller API 2.0.'));
                    // Show Modal or perform any other action
                    if (response && response.type === 'showPasswordModal') {
                        setShowPasswordData(response);
                        setShowPasswordModal(true);
                        return;
                    }
                    if (Array.isArray(response)) {
                        response.forEach((item) => {
                            if (item.type === 'insertValue') {
                                const event = {
                                    target: {
                                        name: item.target,
                                        value: item.value
                                    }
                                };
                                onChange(event);
                                handleShowNotification(setNotification, 'success', t('Successful'), t('Value inserted successfully.'));
                            }
                        });
                    } else if (response && response.type === 'insertValue') {
                        const event = {
                            target: {
                                name: response.target,
                                value: response.value
                            }
                        };
                        onChange(event);
                        handleShowNotification(setNotification, 'success', t('Successful'), t('Value inserted successfully.'));
                    } else {
                        handleShowNotification(setNotification, 'success', t('Successful'), t(response));
                    }
                }
            });
        }
    };

    return (
        <Fragment>
            {notification.show && (
                <Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
            )}
            {formData.map((card, index) => (
                <Card className="mb-4" key={index}>
                    <Card.Header>{card.cardTitle}</Card.Header>
                    <Card.Body>
                        {card.fields
                            .filter((field) => !!field.name) // Skip fields with no name
                            //.filter((field) => field.type && field.type !== 'button') // Skip fields with no type or type 'button'
                            .map((field) => {
                                // Determine value
                                let value;
                                if (
                                    formValues &&
                                    typeof formValues === 'object' &&
                                    !Array.isArray(formValues) &&
                                    formValues.hasOwnProperty(field.name)
                                ) {
                                    // formValues is an object with field values
                                    value = formValues[field.name];
                                } else if (
                                    formValues &&
                                    Array.isArray(formValues) &&
                                    field.formValuesIndex !== undefined &&
                                    formValues[field.formValuesIndex] &&
                                    formValues[field.formValuesIndex].configuration &&
                                    formValues[field.formValuesIndex].configuration.hasOwnProperty(field.name)
                                ) {
                                    // formValues is an array, use configuration object at given index
                                    value = formValues[field.formValuesIndex].configuration[field.name];
                                } else if (
                                    field.fromFlowValue !== undefined &&
                                    flow &&
                                    flow.hasOwnProperty(field.fromFlowValue)
                                ) {
                                    value = flow[field.fromFlowValue];
                                } else if (field.defaultValue !== undefined) {
                                    value = field.defaultValue;
                                } else {
                                    value = '';
                                }

                                if (field.type === 'button') {
                                    return (
                                        <div className="" key={field.name}>
                                            <Form.Label className="d-flex align-items-center">
                                                {field.label}
                                                {field.required && <span style={{ color: 'red' }}> *</span>}
                                            </Form.Label>
                                            {field.description && (
                                                <div className="text-muted mb-2" style={{ fontSize: '0.9em' }}>
                                                    {field.description}
                                                </div>
                                            )}
                                            <Button
                                                variant={field.color || 'primary'}
                                                name={field.name}
                                                onClick={() => handleButtonClick(field)}
                                            >
                                                {field.label}
                                            </Button>
                                        </div>
                                    );
                                }

                                return (
                                    <Form.Group className="mb-4" controlId={field.name} key={field.name}>
                                        <Form.Label>
                                            {field.label}
                                            {field.required && <span style={{ color: 'red' }}> *</span>}
                                        </Form.Label>
                                        {field.description && (
                                            <div className="text-muted mb-2" style={{ fontSize: '0.9em' }}>
                                                {field.description}
                                            </div>
                                        )}
                                        {(field.type === 'text' || field.type === 'email' || field.type === 'password' || field.type === 'number') ? (
                                            field.copyAble ? (
                                                <div className="input-group">
                                                    <Form.Control
                                                        type={field.type}
                                                        name={field.name}
                                                        placeholder={field.placeholder}
                                                        value={value}
                                                        onChange={onChange}
                                                        required={field.required}
                                                        minLength={field.minLength}
                                                        min={field.min}
                                                        max={field.max}
                                                        disabled={field.disabled}
                                                        plaintext={field.plaintext}
                                                        readOnly={field.readOnly}
                                                        isValid={formValues && formValues[field.name + '_copied']}
                                                    />
                                                    <span
                                                        className="input-group-text"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(value);
                                                            if (onChange) {
                                                                // Mark field as valid after copy
                                                                const event = {
                                                                    target: {
                                                                        name: field.name + '_copied',
                                                                        value: true,
                                                                    }
                                                                };
                                                                onChange(event);
                                                                // Reset valid after 5 seconds
                                                                setTimeout(() => {
                                                                    const resetEvent = {
                                                                        target: {
                                                                            name: field.name + '_copied',
                                                                            value: false,
                                                                        }
                                                                    };
                                                                    onChange(resetEvent);
                                                                }, 3000);
                                                            }
                                                        }}
                                                        tabIndex={-1}
                                                        aria-label={t('Copy to clipboard')}
                                                    >
                                                        {t('Copy')}
                                                    </span>
                                                </div>
                                            ) : (
                                                <Form.Control
                                                    type={field.type}
                                                    name={field.name}
                                                    placeholder={field.placeholder}
                                                    value={value}
                                                    onChange={onChange}
                                                    required={field.required}
                                                    minLength={field.minLength}
                                                    min={field.min}
                                                    max={field.max}
                                                    disabled={field.disabled}
                                                    plaintext={field.plaintext}
                                                    readOnly={field.readOnly}
                                                />
                                            )
                                        ) : field.type === 'select' ? (
                                            <Form.Select
                                                name={field.name}
                                                value={value}
                                                onChange={onChange}
                                                required={field.required}
                                                placeholder={field.placeholder}
                                                disabled={field.disabled}
                                            >
                                                <option value="">Select {field.label}</option>
                                                {(field.options || []).map((option) =>
                                                    typeof option === 'object' ? (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ) : (
                                                        <option key={option} value={option}>
                                                            {option}
                                                        </option>
                                                    )
                                                )}
                                            </Form.Select>
                                        ) : field.type === 'checkbox' ? (
                                            <Form.Check
                                                type="checkbox"
                                                name={field.name}
                                                label={field.label}
                                                checked={
                                                    formValues && formValues[field.name] !== undefined
                                                        ? formValues[field.name]
                                                        : field.defaultValue !== undefined
                                                            ? field.defaultValue
                                                            : false
                                                }
                                                onChange={onChange}
                                                required={field.required}
                                                disabled={field.disabled}
                                            />
                                        ) : field.type === 'textarea' ? (
                                            <Form.Control
                                                as="textarea"
                                                name={field.name}
                                                placeholder={field.placeholder}
                                                value={value}
                                                onChange={onChange}
                                                required={field.required}
                                                rows={field.rows}
                                                disabled={field.disabled}
                                            />
                                        ) : null}
                                    </Form.Group>
                                );
                            })}
                    </Card.Body>
                </Card>
            ))}
            <ModalShowPassword
                show={showPasswordModal}
                onHide={() => {
                    setShowPasswordModal(false);
                    setShowPasswordData(null);
                }}
                data={showPasswordData}
                setNotification={setNotification}
            />
        </Fragment>
    );
};

export default DynamicForm;